import React, { useState, useEffect } from "react";
import { usersApi } from "../services/users";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { usePushSubscription } from "../hooks/usePushSubscription";
import { sendInstallEmail } from "../lib/pushClient";
import {
  NATIVE_PUSH_SUPPORTED,
  getNativePushPermission,
  enableNativePush,
  disableNativePush,
  isNativePushOptedOut,
} from "../lib/nativePush";
import ShareIcon from "./ShareIcon";
import BrandLogo from "./BrandLogo";
import NotificationsOffPopup from "./NotificationsOffPopup";
import { t } from "../i18n";
import { T } from "../i18n/T";

interface Props {
  userSlug: string;
  initialWebPushEnabled: boolean;
  onChange?: (enabled: boolean) => void;
  /** Fired whenever the actual receive-push state changes (preference AND active subscription on this device). */
  onActiveChange?: (active: boolean) => void;
}

const NotificationSettings: React.FC<Props> = ({
  userSlug,
  initialWebPushEnabled,
  onChange,
  onActiveChange,
}) => {
  const [webPushEnabled, setWebPushEnabled] = useState<boolean>(initialWebPushEnabled);
  const [saving, setSaving] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const { isDesktop, canInstall, installed, isSafari, isIOS, promptInstall } = useInstallPrompt();
  const {
    supported,
    permission,
    subscribed,
    busy,
    error,
    enable,
    disable,
  } = usePushSubscription({ enabled: webPushEnabled });

  // --- Native (iOS/Android app) push ---
  const isNative = NATIVE_PUSH_SUPPORTED;
  const [nativeEnabled, setNativeEnabled] = useState(false);
  const [nativePerm, setNativePerm] = useState<
    "granted" | "denied" | "prompt" | "unsupported"
  >("unsupported");
  const [nativeBusy, setNativeBusy] = useState(false);
  const [nativeError, setNativeError] = useState<string | null>(null);
  // Toggle-ON while the OS permission is denied → the "Notifications are off"
  // popup (Apple's own prompt is one-shot; the popup deep-links to Settings).
  const [showDeniedPopup, setShowDeniedPopup] = useState(false);

  // Surface the *actual* receive-push state up to the parent so it can render
  // an accurate status badge. Native and web are mutually exclusive surfaces.
  useEffect(() => {
    if (isNative) return;
    onActiveChange?.(webPushEnabled && subscribed);
  }, [isNative, webPushEnabled, subscribed, onActiveChange]);

  useEffect(() => {
    if (!isNative) return;
    onActiveChange?.(nativeEnabled);
  }, [isNative, nativeEnabled, onActiveChange]);

  useEffect(() => {
    if (!isNative) return;
    (async () => {
      const p = await getNativePushPermission();
      setNativePerm(p);
      setNativeEnabled(p === "granted" && !isNativePushOptedOut());
    })();
  }, [isNative]);

  const handleNativeToggle = async (checked: boolean) => {
    console.log("[native-push] toggle tapped ->", checked);
    setNativeBusy(true);
    setNativeError(null);
    try {
      if (checked) {
        const status = await enableNativePush();
        setNativePerm(status);
        setNativeEnabled(status === "granted");
        if (status === "denied") {
          setShowDeniedPopup(true);
        } else if (status === "prompt" || status === "unsupported") {
          setNativeError(t("notificationSettings.couldnTTurnOn"));
        }
      } else {
        await disableNativePush();
        setNativeEnabled(false);
      }
    } catch (err) {
      console.error("[native-push] toggle failed", err);
      setNativeError(t("notificationSettings.couldnTUpdateNotifications"));
    } finally {
      setNativeBusy(false);
    }
  };

  const persistToggle = async (next: boolean) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("isWebPushEnabled", String(next));
      await usersApi.update(userSlug, fd);
      onChange?.(next);
    } catch (err) {
      console.error("[notification settings] save failed", err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (checked: boolean) => {
    setWebPushEnabled(checked);
    if (checked) {
      const ok = await enable();
      if (!ok) {
        setWebPushEnabled(false);
        return;
      }
    } else {
      await disable();
    }
    await persistToggle(checked);
  };

  const handleEmailInstall = async () => {
    setEmailBusy(true);
    setEmailError(null);
    try {
      await sendInstallEmail();
      setEmailSent(true);
    } catch (err: any) {
      setEmailError(
        err?.response?.data?.msg || t("notificationSettings.couldnTSendThe")
      );
    } finally {
      setEmailBusy(false);
    }
  };

  // On the native iOS/Android app, web push isn't available — render the
  // "Push Notifications" head + switch instead of the desktop install/web-push
  // UI. Everything is inline-styled ON PURPOSE: this renders inside ProfilePage,
  // whose `.profile-form input/label` rules restyle checkboxes into text boxes
  // (and broke tapping entirely) — a button-based switch with inline styles is
  // immune to that class of bleed.
  if (isNative) {
    return (
      <div>
        {/* Same anatomy as every Settings row: [line icon · title · one grey
            line · switch] (build 38 — the "Recommended" tag and the box inside
            the card are gone). */}
        <div style={nativeHeadRowStyle}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#24C961" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: "0 0 26px" }}>
            {/* Bell — the Settings rows' icon language (founder, round 4). */}
            <path d="M18 16V10a6 6 0 1 0-12 0v6l-1.5 2.5h15L18 16Z" /><path d="M10 21a2 2 0 0 0 4 0" />
          </svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* The Settings rows' own classes, so this row grows with them on
                taller phones (plain divs — the .profile-form input/label bleed
                that forced inline styles here never touched these). */}
            <div className="profile-setting-label">{t("notificationSettings.notifications")}</div>
            <div className="profile-setting-sub">{t("notificationSettings.receivePushNotifications")}</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={nativeEnabled}
            aria-label={t("notificationSettings.pushNotificationsOnThis")}
            disabled={nativeBusy}
            onClick={() => handleNativeToggle(!nativeEnabled)}
            style={{
              ...nativeSwitchStyle,
              background: nativeEnabled ? "#24C961" : "rgba(255,255,255,0.14)",
              opacity: nativeBusy ? 0.6 : 1,
            }}
          >
            <span
              style={{
                ...nativeKnobStyle,
                transform: nativeEnabled ? "translateX(18px)" : "translateX(0)",
              }}
            />
          </button>
        </div>
        {nativePerm === "denied" && (
          <div style={warningTextStyle}>
            {t("notificationSettings.notificationsAreOffFor")}
          </div>
        )}
        {nativeError && <div style={warningTextStyle}>{nativeError}</div>}
        <NotificationsOffPopup
          open={showDeniedPopup}
          onClose={() => setShowDeniedPopup(false)}
        />
      </div>
    );
  }

  return (
    <div style={sectionStyle}>
      {!supported && (
        // TODO(domain-rename): when we move from requesta.app to request.app,
        // update the visible string below. Same string in any marketing copy
        // pointing fans to the desktop install flow.
        <div style={helpTextStyle}>
          <T k="notificationSettings.forSetupOpenWww" tags={{ 0: (c) => <strong>{c}</strong> }} />
        </div>
      )}

      {supported && isDesktop && !installed && canInstall && (
        <button
          type="button"
          onClick={promptInstall}
          style={primaryButtonStyle}
        >
          {t("notificationSettings.installRequestAppOn")}
        </button>
      )}

      {supported && isDesktop && !installed && !canInstall && isSafari && (
        <div style={instructionBoxStyle}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{t("notificationSettings.installOnSafari")}</div>
          <ol style={instructionListStyle}>
            <li><T k="notificationSettings.atTheTopOf" tags={{ 0: (c) => <strong>{c}</strong> }} /><ShareIcon /></li>
            <li><T k="notificationSettings.clickAddToDock" tags={{ 0: (c) => <strong>{c}</strong> }} /></li>
            <li>{t("notificationSettings.openRequestAppFrom")}</li>
          </ol>
        </div>
      )}

      {supported && isDesktop && !installed && !canInstall && !isSafari && (
        <div style={helpTextStyle}>
          {t("notificationSettings.openRequestAppIn")}
        </div>
      )}

      {supported && (
        <div style={toggleRowStyle}>
          <input
            id="web-push-check"
            type="checkbox"
            checked={webPushEnabled && subscribed}
            onChange={(e) => handleToggle(e.target.checked)}
            disabled={busy || saving}
            style={{ width: 18, height: 18 }}
          />
          <label htmlFor="web-push-check" style={toggleLabelStyle}>
            <T k="notificationSettings.sendRequestsToMy" tags={{ 0: (c) => <span style={recommendedTagStyle}>{c}</span> }} />
            {busy && <span style={{ opacity: 0.6, marginLeft: 8 }}>…</span>}
          </label>
        </div>
      )}

      {supported && webPushEnabled && permission === "denied" && (
        <div style={warningTextStyle}>
          {t("notificationSettings.notificationsAreBlockedIn")}
        </div>
      )}

      {error && <div style={warningTextStyle}>{error}</div>}

      {!isDesktop && !subscribed && !isIOS && (
        <div style={mobileHelpStyle}>
          <div style={{ fontSize: "0.9rem", fontWeight: 600, marginBottom: 4 }}>
            <T k="notificationSettings.requestAppDesktopRecommended" tags={{ 0: (c) => <span style={recommendedTagStyle}>{c}</span> }} />
          </div>
          <div style={{ fontSize: "0.82rem", color: "#9ca3af", marginBottom: 10, lineHeight: 1.4 }}>
            {t("notificationSettings.getNotificationsRightOn")}
          </div>
          <button
            type="button"
            onClick={handleEmailInstall}
            disabled={emailBusy || emailSent}
            style={emailSent ? secondaryButtonDoneStyle : secondaryButtonStyle}
          >
            {emailSent ? (
              t("notificationSettings.emailSentCheckYour")
            ) : emailBusy ? (
              t("notificationSettings.sending")
            ) : (
              <>
                <BrandLogo variant="icon" alt="" style={buttonLogoStyle} />
                {t("notificationSettings.emailMeInstallLink")}
              </>
            )}
          </button>
          {emailError && <div style={warningTextStyle}>{emailError}</div>}
        </div>
      )}
    </div>
  );
};

const sectionStyle: React.CSSProperties = {
  marginTop: "1rem",
  marginBottom: "0.5rem",
};

const helpTextStyle: React.CSSProperties = {
  fontSize: "0.82rem",
  color: "#9ca3af",
  marginTop: 4,
  lineHeight: 1.4,
};

const warningTextStyle: React.CSSProperties = {
  fontSize: "0.82rem",
  color: "#f87171",
  marginTop: 6,
  lineHeight: 1.4,
};

const toggleRowStyle: React.CSSProperties = {
  marginTop: "0.75rem",
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const toggleLabelStyle: React.CSSProperties = {
  cursor: "pointer",
  fontWeight: "normal",
  fontSize: "0.95rem",
};

const recommendedTagStyle: React.CSSProperties = {
  color: "#a78bfa",
  fontWeight: 600,
  fontSize: "0.82em",
  marginLeft: 4,
};

// Native head row — mirrors the web card heads (Desktop Companion / SMS Alerts)
// visually, but fully inline-styled; see the comment on the isNative branch.
const nativeHeadRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const nativeSwitchStyle: React.CSSProperties = {
  position: "relative",
  width: 44,
  height: 26,
  borderRadius: 13,
  border: "none",
  padding: 2,
  cursor: "pointer",
  transition: "background 0.2s",
  flexShrink: 0,
  WebkitTapHighlightColor: "transparent",
};

const nativeKnobStyle: React.CSSProperties = {
  display: "block",
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: "white",
  transition: "transform 0.2s",
};

const buttonLogoStyle: React.CSSProperties = {
  width: 18,
  height: 18,
  verticalAlign: "middle",
  marginRight: 8,
};

const primaryButtonStyle: React.CSSProperties = {
  background: "#7c3aed",
  color: "white",
  border: "none",
  borderRadius: 8,
  padding: "10px 16px",
  fontWeight: 700,
  fontSize: "0.9rem",
  cursor: "pointer",
  marginTop: 4,
};

const secondaryButtonStyle: React.CSSProperties = {
  background: "rgba(124, 58, 237, 0.12)",
  color: "#a78bfa",
  border: "1px solid rgba(124, 58, 237, 0.35)",
  borderRadius: 8,
  padding: "10px 16px",
  fontWeight: 600,
  fontSize: "0.9rem",
  cursor: "pointer",
  width: "100%",
};

const secondaryButtonDoneStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  background: "rgba(36, 201, 97, 0.12)",
  color: "#4ade80",
  border: "1px solid rgba(36, 201, 97, 0.35)",
  cursor: "default",
};

const mobileHelpStyle: React.CSSProperties = {
  marginTop: "0.75rem",
  padding: "12px",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 10,
  background: "rgba(255,255,255,0.02)",
};

const instructionBoxStyle: React.CSSProperties = {
  marginTop: "0.75rem",
  padding: "12px 14px",
  border: "1px solid rgba(124, 58, 237, 0.25)",
  borderRadius: 10,
  background: "rgba(124, 58, 237, 0.06)",
  fontSize: "0.85rem",
  lineHeight: 1.5,
  color: "#d4d4d8",
};

const instructionListStyle: React.CSSProperties = {
  margin: "0",
  paddingLeft: "1.1rem",
};

export default NotificationSettings;
